import { objectId } from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  contentGeneratorConfigPopulatedSchema,
  contentGeneratorConfigSchema,
} from "./content-generator-config/content-generator-config-definition";

export const GenerationMode = { Agent: "agent", Template: "template" } as const;
export type GenerationMode =
  (typeof GenerationMode)[keyof typeof GenerationMode];

export const ClassifierAction = {
  ScenarioOutreach: "scenario-outreach",
  ScenarioReply: "scenario-reply",
  Fallback: "fallback",
  NoReply: "no-reply",
} as const;
export type ClassifierAction =
  (typeof ClassifierAction)[keyof typeof ClassifierAction];

export const ScenarioType = {
  DiscountOffer: "discount-offer",
  PaymentFailure: "payment-failure",
  SubscriptionUpsell: "subscription-upsell",
  MarketingSubscription: "marketing-subscription",
  Custom: "custom",
} as const;
export type ScenarioType = (typeof ScenarioType)[keyof typeof ScenarioType];

const scenarioBaseSchema_ = z.object({
  _id: objectId().optional(),
  enabled: z.boolean(),
  mode: createEnum(GenerationMode, { enumName: "GenerationMode" }),
  outreachTemplate: z.string(),
  replyTemplate: z.string().nullish(),
  contentGeneratorConfig: contentGeneratorConfigSchema.nullish(),
});

const builtInScenarioSchema_ = scenarioBaseSchema_.extend({
  type: createEnum(
    Object.values(ScenarioType).filter(
      (v): v is Exclude<ScenarioType, "custom"> => v !== ScenarioType.Custom,
    ),
    { enumName: "ScenarioType", isPartial: true, originalEnum: ScenarioType },
  ),
});
interface BuiltInScenario extends z.infer<typeof builtInScenarioSchema_> {}

const customScenarioSchema_ = scenarioBaseSchema_.extend({
  type: z.literal(ScenarioType.Custom),
  name: z.string(),
});
interface CustomScenario extends z.infer<typeof customScenarioSchema_> {}

export const scenarioSchema = z.union([
  builtInScenarioSchema_,
  customScenarioSchema_,
]);
export type Scenario = BuiltInScenario | CustomScenario;

// Populated variants — carry the full draftVersion so the UI's changes
// survive the RPC serialization round-trip and reach the server handler.
const scenarioPopulatedBaseSchema_ = scenarioBaseSchema_
  .omit({ contentGeneratorConfig: true })
  .extend({
    contentGeneratorConfig: contentGeneratorConfigPopulatedSchema.optional(),
  });

const builtInScenarioPopulatedSchema_ = scenarioPopulatedBaseSchema_.extend({
  type: createEnum(
    Object.values(ScenarioType).filter(
      (v): v is Exclude<ScenarioType, "custom"> => v !== ScenarioType.Custom,
    ),
    { enumName: "ScenarioType", isPartial: true, originalEnum: ScenarioType },
  ),
});

const customScenarioPopulatedSchema_ = scenarioPopulatedBaseSchema_.extend({
  type: z.literal(ScenarioType.Custom),
  name: z.string(),
});

export const scenarioPopulatedSchema = z.union([
  builtInScenarioPopulatedSchema_,
  customScenarioPopulatedSchema_,
]);
export type ScenarioPopulated = z.infer<typeof scenarioPopulatedSchema>;

export const fallbackSchema = z.object({
  mode: createEnum(GenerationMode, { enumName: "GenerationMode" }),
  template: z.string(),
  customInstructions: z.string().optional(),
});
const fallbackSchema_ = fallbackSchema;

// ===================================================
//   Main Schema
// ===================================================

const recoverConfigSchema_ = z.object({
  _id: objectId(),
  teamId: objectId(),
  name: z.string(),
  discountId: objectId().nullish(),
  agentName: z.string().optional(),
  brandName: z.string().optional(),
  supportEmail: z.email().optional(),
  /** Array of scenarios from which the classifier selects */
  scenarios: z.array(scenarioSchema),
  /** In the case no scenarios conform to the current conversation, use this as a fallback */
  fallback: fallbackSchema_,
});
export const recoverConfigSchema: z.ZodType<RecoverConfig> =
  recoverConfigSchema_;
export interface RecoverConfig extends z.infer<typeof recoverConfigSchema_> {}

/** Populated variant — scenarios carry full draftVersion for UI display. */
const recoverConfigPopulatedSchema_ = recoverConfigSchema_
  .omit({ scenarios: true })
  .extend({ scenarios: z.array(scenarioPopulatedSchema) });
export const recoverConfigPopulatedSchema: z.ZodType<RecoverConfigPopulated> =
  recoverConfigPopulatedSchema_;
export interface RecoverConfigPopulated
  extends z.infer<typeof recoverConfigPopulatedSchema_> {}

// ===================================================
//   Utils
// ===================================================

export type BuiltInScenarioType = Exclude<ScenarioType, "custom">;

const SCENARIO_LABELS: Record<BuiltInScenarioType, string> = {
  [ScenarioType.DiscountOffer]: "Discount offer",
  [ScenarioType.PaymentFailure]: "Payment failure",
  [ScenarioType.SubscriptionUpsell]: "Subscription upsell",
  [ScenarioType.MarketingSubscription]: "Marketing subscription",
};

const MODE_LABELS: Record<GenerationMode, string> = {
  [GenerationMode.Agent]: "Agent",
  [GenerationMode.Template]: "Template",
};

export function getScenarioLabel(scenario: Scenario): string {
  if (isCustomScenario(scenario)) {
    return scenario.name || "Custom";
  }
  return SCENARIO_LABELS[scenario.type] ?? scenario.type;
}

export function getModeLabel(scenario: Scenario): string {
  return MODE_LABELS[scenario.mode] ?? scenario.mode;
}

export function isBuiltInScenario(
  scenario: Scenario | ScenarioPopulated,
): scenario is BuiltInScenario {
  return scenario.type !== ScenarioType.Custom;
}

export function isCustomScenario(
  scenario: Scenario | ScenarioPopulated,
): scenario is CustomScenario {
  return scenario.type === ScenarioType.Custom;
}

export function getBuiltInScenario(
  scenarios: Scenario[],
  type: BuiltInScenarioType,
): BuiltInScenario | undefined {
  return scenarios.find((s): s is BuiltInScenario => s.type === type);
}

export function getCustomScenarios(scenarios: Scenario[]): CustomScenario[] {
  return scenarios.filter(isCustomScenario);
}

export function getEnabledScenarios(scenarios: Scenario[]): Scenario[] {
  return scenarios.filter((s) => s.enabled);
}

export function getScenarioById(
  scenarios: Scenario[],
  scenarioId: string,
): Scenario | undefined {
  return scenarios.find((s) => s._id?.toString() === scenarioId);
}
